import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Building2, Loader2, Save } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { P, PermissionGuard, RequirePermission } from '@/features/permissions';
import { useOrganization, useUpdateOrganization } from '../api/organization-api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { Skeleton } from '@/components/ui/skeleton';

export function OrganizationPage() {
  return (
    <PermissionGuard permission={P.MANAGE_ORGANIZATION}>
      <OrganizationPageContent />
    </PermissionGuard>
  );
}

function OrganizationPageContent() {
  const setPageHeader = usePageHeaderSetter();
  const { data: org, isLoading } = useOrganization();
  const updateOrg = useUpdateOrganization();

  const [name, setName] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (org) {
      setName(org.name);
    }
  }, [org]);

  const handleNameChange = (value: string) => {
    setName(value);
    setIsDirty(value !== org?.name);
  };

  const handleSave = async () => {
    if (!isDirty || !name.trim()) return;
    try {
      await updateOrg.mutateAsync({ name: name.trim() });
      setIsDirty(false);
      toast.success('Organization updated');
    } catch {
      toast.error('Failed to update organization');
    }
  };

  useEffect(() => {
    setPageHeader({
      title: 'Organization Settings',
      actions: (
        <RequirePermission permission={P.MANAGE_ORGANIZATION}>
          <Button
            size="sm"
            disabled={!isDirty || updateOrg.isPending || !name.trim()}
            onClick={handleSave}
          >
            {updateOrg.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="hidden md:inline">Save</span>
          </Button>
        </RequirePermission>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, isDirty, updateOrg.isPending, name]);

  if (isLoading) {
    return (
      <div className="mt-4 px-4">
        <div className="mx-auto max-w-2xl space-y-6">
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">Organization not found</p>
      </div>
    );
  }

  return (
    <div className="mt-4 px-4">
      <div className="mx-auto max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base">General</CardTitle>
                <CardDescription>Manage your organization details</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter organization name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Organization ID</Label>
              <p className="rounded-md bg-muted px-3 py-2 font-mono text-sm">
                {org.id}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Created</Label>
                <p className="text-sm">
                  {format(new Date(org.created_at), 'MMM d, yyyy')}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Last Updated</Label>
                <p className="text-sm">
                  {format(new Date(org.updated_at), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
